import styled, { keyframes } from 'styled-components';

const glowPulse = keyframes`
  0% { box-shadow: 0 0 10px rgba(64, 128, 255, 0.4); }
  50% { box-shadow: 0 0 20px rgba(0, 255, 255, 0.7), 0 0 30px rgba(64, 128, 255, 0.5); }
  100% { box-shadow: 0 0 10px rgba(64, 128, 255, 0.4); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const techGridPulse = keyframes`
  0% { opacity: 0.1; }
  50% { opacity: 0.3; }
  100% { opacity: 0.1; }
`;

const digitalCircuitFlow = keyframes`
  0% { background-position: 0% 0%; }
  100% { background-position: 100% 100%; }
`;

const scanLine = keyframes`
  0% { transform: translateY(-100%); opacity: 0.5; }
  100% { transform: translateY(100%); opacity: 0; }
`;

const energyRing = keyframes`
  0% { transform: scale(1); opacity: 0.5; }
  50% { transform: scale(1.2); opacity: 0.8; }
  100% { transform: scale(1); opacity: 0.5; }
`;

export const PlatformSection = styled.div`
  margin-top: 3rem;
  padding: 2rem;
  position: relative;
  background: rgba(0, 16, 32, 0.7);
  border-radius: 16px;
  border: 1px solid rgba(64, 128, 255, 0.3);
  box-shadow: 0 0 20px rgba(0, 64, 255, 0.2);
  overflow: hidden;
  animation: ${glowPulse} 5s infinite ease-in-out;
  
  /* Efeito de scanner */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 10px;
    background: linear-gradient(to bottom, 
      rgba(0, 255, 255, 0.5), 
      rgba(0, 255, 255, 0)
    );
    animation: ${scanLine} 4s infinite ease-in-out;
    z-index: 1;
  }
  
  /* Background de circuito digital */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M10 10h10v10H10zM30 10h10v10H30zM50 10h10v10H50zM70 10h10v10H70zM90 10h10v10H90zM10 30h10v10H10zM30 30h10v10H30zM50 30h10v10H50zM70 30h10v10H70zM90 30h10v10H90zM10 50h10v10H10zM30 50h10v10H30zM50 50h10v10H50zM70 50h10v10H70zM90 50h10v10H90zM10 70h10v10H10zM30 70h10v10H30zM50 70h10v10H50zM70 70h10v10H70zM90 70h10v10H90zM10 90h10v10H10zM30 90h10v10H30zM50 90h10v10H50zM70 90h10v10H70zM90 90h10v10H90z' fill='none' stroke='rgba(0, 255, 255, 0.05)' stroke-width='1'/%3E%3Cpath d='M0 20h100M0 40h100M0 60h100M0 80h100M20 0v100M40 0v100M60 0v100M80 0v100' stroke='rgba(0, 255, 255, 0.03)' stroke-width='1'/%3E%3C/svg%3E");
    background-size: 50px 50px;
    opacity: 0.3;
    z-index: -1;
    animation: ${techGridPulse} 4s infinite ease-in-out, ${digitalCircuitFlow} 20s infinite linear;
  }
`;

export const PlatformTitle = styled.h3`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.5rem;
  font-weight: 600;
  text-align: center;
  margin-bottom: 2rem;
  letter-spacing: 2px;
  color: #FFFFFF;
  text-shadow: 0 0 10px rgba(0, 255, 255, 0.5);
  position: relative;
  
  /* Linhas tecnológicas decorativas */
  &::before, &::after {
    content: '';
    position: absolute;
    height: 2px;
    background: linear-gradient(90deg, 
      rgba(0, 32, 64, 0), 
      rgba(0, 255, 255, 0.8), 
      rgba(0, 32, 64, 0)
    );
    width: 80%;
    left: 10%;
  }
  
  &::before {
    top: -10px;
  }
  
  &::after {
    bottom: -10px;
  }
`;

export const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  padding-top: 56.25%; /* 16:9 Aspect Ratio */
  margin: 2rem auto;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(0, 255, 255, 0.3);
  box-shadow: 0 0 15px rgba(0, 64, 255, 0.3);
  animation: ${fadeIn} 1s ease-out;
  max-width: 800px;
  background-image: url('/thumb.png');
  background-size: cover;
  background-position: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 16, 32, 0.2);
    z-index: 1;
    pointer-events: none;
  }
`;

export const VideoOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 16, 32, 0.5);
  z-index: 2;
  cursor: pointer;
  transition: all 0.3s ease;
  
  ${props => props.isPlaying && `
    opacity: 0;
    pointer-events: none;
  `}
  
  &:hover {
    background: rgba(0, 16, 32, 0.3);
  }
`;

export const PlayButton = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(0, 32, 64, 0.7);
  border: 2px solid rgba(0, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 0 20px rgba(0, 255, 255, 0.5);
  position: relative;
  
  /* Efeito de círculos concêntricos */
  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: 1px solid rgba(0, 255, 255, 0.3);
    animation: ${energyRing} 4s infinite ease-in-out;
  }
  
  /* Triângulo de play com efeito de brilho */
  &::before {
    content: '';
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 15px 0 15px 25px;
    border-color: transparent transparent transparent rgba(0, 255, 255, 0.9);
    margin-left: 5px;
    filter: drop-shadow(0 0 5px rgba(0, 255, 255, 0.8));
  }
  
  &:hover {
    transform: scale(1.1);
    background: rgba(0, 64, 128, 0.7);
    box-shadow: 0 0 30px rgba(0, 255, 255, 0.7);
  }
`;

export const VideoFrame = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: none;
  z-index: 1;
`;

export const VideoDescription = styled.p`
  text-align: center;
  color: rgba(255, 255, 255, 0.8);
  font-size: 1rem;
  margin: 1.5rem auto;
  max-width: 700px;
  line-height: 1.6;
  animation: ${fadeIn} 1.2s ease-out;
`;
